import { useEffect, useMemo } from 'react';

import { useDisclosureOpenState } from '~/shared/components/filters/hooks';

import type { ExplorationVolume, ViewMode } from '../../types/exploration';
import ExplorationFilterSection from './ExplorationFilterSection';
import { dimensionDefaultOpen, explorationDimensions } from './dimensions';
import { isCapacityRangeNarrowed } from '~/shared/components/filters/capacityFormat';
import type { CapacityBounds, ExplorationFilterPanelState } from './utils/filterConverters';
import {
  buildSectionOptions,
  computeSectionVisibility,
  type SectionId,
} from './utils/sectionVisibility';

interface FilterPanelContentProps {
  view: ViewMode;
  panelOpen?: boolean;
  filterSearchQuery?: string;
  volumes: ExplorationVolume[];
  capacityBounds: CapacityBounds;
  pending: ExplorationFilterPanelState;
  setField: <K extends keyof ExplorationFilterPanelState>(
    key: K
  ) => (value: ExplorationFilterPanelState[K]) => void;
}

export default function FilterPanelContent({
  view,
  panelOpen = false,
  filterSearchQuery = '',
  volumes,
  capacityBounds,
  pending,
  setField,
}: FilterPanelContentProps) {
  const disclosures = useDisclosureOpenState<SectionId>(dimensionDefaultOpen);

  const sectionOptions = useMemo(() => buildSectionOptions(volumes), [volumes]);

  const sectionVisible = useMemo(
    () => computeSectionVisibility(sectionOptions, { view, query: filterSearchQuery }),
    [filterSearchQuery, sectionOptions, view]
  );

  /** Sections that already carry a selection start expanded, once per panel-open. */
  const sectionsWithSelection = useMemo(() => {
    const map: Partial<Record<SectionId, boolean>> = {
      capacity: isCapacityRangeNarrowed(pending.capacityMin, pending.capacityMax, capacityBounds),
      support: pending.supportStatuses.length > 0,
    };
    explorationDimensions.forEach((d) => {
      if (d.kind !== 'checkbox') return;
      map[d.id] = d.codec.count(pending[d.stateKey]) > 0;
    });
    return map;
  }, [pending, capacityBounds]);

  const { initializeOnce, openSection } = disclosures;

  useEffect(() => {
    initializeOnce(panelOpen, sectionsWithSelection);
  }, [panelOpen, sectionsWithSelection, initializeOnce]);

  /** A global query that matches a searchable section expands it so the hits are visible. */
  useEffect(() => {
    if (!filterSearchQuery.trim()) return;
    explorationDimensions.forEach((d) => {
      if (d.respondsToGlobalSearch && sectionVisible[d.id]) openSection(d.id);
    });
  }, [filterSearchQuery, sectionVisible, openSection]);

  return (
    <div className="flex-1 overflow-y-auto pb-4">
      {explorationDimensions.map((dimension) =>
        sectionVisible[dimension.id] ? (
          <ExplorationFilterSection
            key={dimension.id}
            dimension={dimension}
            pending={pending}
            setField={setField}
            sectionOptions={sectionOptions}
            capacityBounds={capacityBounds}
            filterSearchQuery={filterSearchQuery}
            panelOpen={panelOpen}
            open={disclosures.isOpen(dimension.id)}
            onOpenChange={disclosures.setOpen(dimension.id)}
          />
        ) : null
      )}
    </div>
  );
}
